import type { ProgrammaticLandingPageConfig } from '../../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from '../storageSizeFacts'

export const KWH_7_MAGAZYN_ENERGII: ProgrammaticLandingPageConfig = {
  slug: 'magazyn-energii-7-kwh-cena',
  lang: 'pl',
  topicId: 'kwh-speicher-7',
  title: 'Magazyn energii 7 kWh: cena i wartości orientacyjne 2026',
  metaDescription:
    'Ile kosztuje magazyn energii 7 kWh? Dla kogo się nadaje, wartości orientacyjne ' +
    'autokonsumpcji i bezpłatne porównanie ofert na instalację + magazyn.',
  keywords: [
    'magazyn energii 7 kWh cena',
    'magazyn energii 7 kWh koszt',
    'bateria fotowoltaiczna 7 kWh',
    'ile kosztuje magazyn energii 7 kWh',
  ],
  eyebrow: 'Wielkość magazynu: 7 kWh',
  h1: 'Magazyn energii 7 kWh: cena i wartości orientacyjne',
  intro:
    'Magazyn energii 7 kWh to popularna wielkość dla przeciętnych gospodarstw domowych, w ' +
    `połączeniu z referencyjną instalacją ${STORAGE_REFERENCE_KWP} kWp. Znajdziesz tu wartości ` +
    'orientacyjne dotyczące odpowiedniej wielkości gospodarstwa, autokonsumpcji z magazynem ' +
    'energii, a tuż poniżej kalkulator z już wypełnioną średnią ceną dla tego połączenia, ' +
    'obliczoną na podstawie prawdziwych ofert firm rzemieślniczych.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 7 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Dla kogo odpowiedni jest magazyn energii 7 kWh?' },
    {
      type: 'paragraph',
      text:
        'Wartość orientacyjna: 7 kWh zwykle pasuje do przeciętnych gospodarstw 3-4-osobowych bez ' +
        'pompy ciepła i samochodu elektrycznego. Kto dodatkowo częściowo ładuje samochód ' +
        'elektryczny w domu lub ma wyższe zużycie wieczorem, znajdzie kolejną wielkość na stronie ' +
        '12 kWh.',
    },
    { type: 'heading', text: 'Autokonsumpcja z magazynem energii' },
    {
      type: 'paragraph',
      text:
        `Bez magazynu udział autokonsumpcji w instalacji ${STORAGE_REFERENCE_KWP} kWp wynosi ` +
        `zwykle ok. ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %; z magazynem energii - w modelu ` +
        `obliczeniowym o ok. ${SELF_CONSUMPTION_BATTERY_BONUS * 100} punktów procentowych więcej, ` +
        `do ok. ${SELF_CONSUMPTION_WITH_BATTERY * 100} % - przy samochodzie elektrycznym lub pompie ` +
        'ciepła w gospodarstwie udział może dalej rosnąć, aż do granicy 85 %. Dokładne obliczenie ' +
        'dla Twojego zużycia daje kalkulator doboru instalacji.',
    },
    { type: 'heading', text: 'Zasilanie awaryjne' },
    {
      type: 'paragraph',
      text:
        'Przy 7 kWh pojemność wielu skrzynek backup do zasilania awaryjnego wystarcza już na kilka ' +
        'centralnych odbiorników przez dłuższy czas, choć niekoniecznie na cały dom - zależy to od ' +
        'rzeczywistego zużycia podłączonych urządzeń. Czy i w jakim zakresie dana oferta obejmuje ' +
        'zasilanie awaryjne, zależy od konkretnej firmy - warto o to zapytać bezpośrednio.',
    },
    { type: 'heading', text: 'Koszty magazynu i dofinansowanie' },
    {
      type: 'paragraph',
      text:
        'Koszty magazynów energii mocno się różnią w zależności od producenta i złożoności ' +
        'montażu - badanie rynku z 2026 roku pokazuje przedział od ok. ' +
        `${BATTERY_COST_PER_KWH_RANGE_EUR.min} do ${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('pl-PL')} ` +
        '€ za kWh. W Niemczech istnieją regionalne dofinansowania specjalnie na magazyny energii ' +
        '(np. program kraju związkowego Berlin z dopłatą do magazynów energii); w Austrii system ' +
        'EAG dolicza 150 €/kWh za magazyn - dokładne obliczenie dla Twojego połączenia daje ' +
        'kalkulator poniżej.',
    },
  ],
  faq: [
    {
      q: 'Czy 7 kWh to odpowiednia wielkość dla standardowego gospodarstwa?',
      a: 'Dla przeciętnego gospodarstwa 3-4-osobowego bez pompy ciepła i samochodu elektrycznego 7 kWh to popularna wielkość. Przy dodatkowych dużych odbiornikach warto rozważyć 12 lub 15 kWh.',
    },
    {
      q: 'Czy 7 kWh wystarczy na pełną samowystarczalność w nocy?',
      a: 'W wielu przypadkach tak, przy przeciętnym zużyciu bez pompy ciepła czy ładowania samochodu elektrycznego. Dokładną ocenę dla Twojego rzeczywistego zużycia daje kalkulator doboru instalacji.',
    },
    {
      q: 'Z jaką wielkością instalacji łączony jest zwykle magazyn 7 kWh?',
      a: `Na tej stronie jako punkt odniesienia przyjęto instalację ${STORAGE_REFERENCE_KWP} kWp. Przy nieco mniejszych instalacjach 7 kWh również zwykle dobrze pasuje - dokładną rekomendację dla Twojego dachu daje kalkulator doboru instalacji.`,
    },
  ],
  primaryCta: { label: 'Poproś teraz o bezpłatną ofertę', action: 'customer' },
  secondaryCta: {
    label: 'Koszt instalacji fotowoltaicznej: pełny przewodnik',
    action: 'link',
    href: '/koszt-instalacji-fotowoltaicznej',
  },
  relatedLinks: [
    { label: 'Magazyn energii 5 kWh: cena i wartości orientacyjne', href: '/magazyn-energii-5-kwh-cena' },
    { label: 'Magazyn energii 12 kWh: cena i wartości orientacyjne', href: '/magazyn-energii-12-kwh-cena' },
    { label: 'Instalacja fotowoltaiczna 10 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-10-kwp-cena' },
    { label: 'Jak działa porównanie ofert', href: '/porownaj-oferty-fotowoltaiczne' },
  ],
}
